/**
 * Abstract base for input validation. Implement validate(value) and provide errorText
 * shown when validation fails. Used by the Input component's validators prop.
 */
export abstract class InputValidator {
  public errorText: string;

  protected constructor(errorText: string) {
    this.errorText = errorText;
  }

  public abstract validate(value: string): boolean;
}

const emailValidatorRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** Validates email format. Use with Input validators prop; default error message: "Email format is incorrect". */
export class EmailValidator extends InputValidator {
  constructor(errorText = 'Email format is incorrect') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return emailValidatorRegex.test(value);
  }
}

export class StringLengthValidator extends InputValidator {
  private readonly minCharacters: number;
  private readonly maxCharacters: number;

  constructor(
    minCharacters: number = Number.MAX_SAFE_INTEGER,
    maxCharacters: number = 0,
    errorText: string
  ) {
    super(errorText);
    this.minCharacters = minCharacters;
    this.maxCharacters = maxCharacters;
  }

  public validate(value: string): boolean {
    const length = value.replace(/ /g, '').length;
    return length <= this.maxCharacters && length >= this.minCharacters;
  }
}

export class StringLengthWithSpecialCharacterValidator extends InputValidator {
  private readonly minLength: number;
  private readonly maxLength: number;

  // default regex accapt only number, alphabets, spaces and hyphen
  private readonly acceptedCharactersRegex: RegExp = new RegExp('^[a-zA-Z0-9\\s-]+$');

  constructor(
    minLength: number,
    maxLength: number,
    specialCharactersRegex?: RegExp,
    errorText: string = 'Invalid input'
  ) {
    super(errorText);
    this.minLength = minLength;
    this.maxLength = maxLength;
    /* if special characters regex is provided, use it instead of the default regex */
    if (specialCharactersRegex) {
      this.acceptedCharactersRegex = specialCharactersRegex;
    }
  }

  public validate(value: string): boolean {
    /* trim the value to remove leading and trailing spaces */
    value = value.trim();
    return (
      value.length >= this.minLength &&
      value.length <= this.maxLength &&
      this.acceptedCharactersRegex.test(value)
    );
  }
}

/** Validates credit card number (13-19 digits with spaces allowed). */
export class CardNumberValidator extends InputValidator {
  constructor(errorText = 'Invalid card number') {
    super(errorText);
  }

  public validate(value: string): boolean {
    // Remove spaces and check if it's 13-19 digits
    const digitsOnly = value.replace(/\s/g, '');
    return /^\d{13,19}$/.test(digitsOnly);
  }
}

/** Validates CVV (3-4 digits). */
export class CVVValidator extends InputValidator {
  constructor(errorText = 'Invalid CVV') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return /^\d{3,4}$/.test(value);
  }
}

/** Validates expiry date in MM/YY format and checks if not expired. */
export class ExpiryDateValidator extends InputValidator {
  constructor(errorText = 'Invalid or expired date') {
    super(errorText);
  }

  public validate(value: string): boolean {
    // Check format MM/YY
    if (!/^\d{2}\/\d{2}$/.test(value)) {
      return false;
    }

    const [month, year] = value.split('/').map(Number);

    // Validate month (01-12)
    if (month < 1 || month > 12) {
      return false;
    }

    // Check if not expired
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
    const currentMonth = currentDate.getMonth() + 1; // 0-indexed

    if (year < currentYear) {
      return false; // Expired year
    }

    if (year === currentYear && month < currentMonth) {
      return false; // Expired month
    }

    return true;
  }
}

/** Requires two tokens: a one-line name is split before it reaches the API, so one token persists an empty `lastName`. */
export class FullNameValidator extends InputValidator {
  constructor(errorText = 'Please enter both a first and last name') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return value.trim().split(/\s+/).filter(Boolean).length >= 2;
  }
}

/** Validates US phone number (10 digits, ignoring formatting characters). */
export class PhoneValidator extends InputValidator {
  constructor(errorText = 'Please enter a valid 10-digit phone number') {
    super(errorText);
  }

  public validate(value: string): boolean {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length === 10;
  }
}

/** Validates a US bank routing number (exactly 9 digits, ignoring non-digit characters). */
export class RoutingNumberValidator extends InputValidator {
  constructor(errorText = 'Should have 9 digits') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return /^\d{9}$/.test(value.replace(/\D/g, ''));
  }
}

/** Validates US zipcode (5 digits). */
export class ZipcodeValidator extends InputValidator {
  constructor(errorText = 'Invalid zipcode') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return /^\d{5}$/.test(value);
  }
}

/** Validates that the value is either a valid email or a valid US phone number (with optional extension). */
const phoneWithExtnRegex =
  /^[+]?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})([\s][x]([0-9]{3,12}))?$/;

export class EmailOrPhoneValidator extends InputValidator {
  constructor(errorText = 'Please enter a valid email or phone number') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return emailValidatorRegex.test(value) || phoneWithExtnRegex.test(value);
  }
}

/**
 * Validates a 4-digit calendar year for vehicle model-year inputs (e.g. the damage-claim
 * incident report). Accepts a 4-digit year between `minYear` (default 1900) and the next
 * calendar year, inclusive; rejects years beyond next year, non-numeric input, and values
 * that are not exactly 4 digits.
 *
 * ## Invariants:
 * - The ceiling is the current year + 1, not the current year.
 *   Why: vehicle model years are released over the prior summer (e.g. 2027 models go on
 *   sale in mid-2026), so next calendar year is a legitimate model year for much of the year.
 */
export class YearValidator extends InputValidator {
  private readonly minYear: number;

  constructor(errorText = 'Please enter a valid year', minYear = 1900) {
    super(errorText);
    this.minYear = minYear;
  }

  public validate(value: string): boolean {
    const trimmed = value.trim();
    if (!/^\d{4}$/.test(trimmed)) {
      return false;
    }
    const year = Number(trimmed);
    // Next model year is valid — model years ship over the prior summer.
    const maxYear = new Date().getFullYear() + 1;
    return year >= this.minYear && year <= maxYear;
  }
}

/**
 * Validates that a currency string parses to a number within [min, max].
 * Strips currency symbols/commas before parsing. Empty / non-numeric ⇒ invalid.
 */
export class CurrencyRangeValidator extends InputValidator {
  private readonly min: number;
  private readonly max: number;

  constructor(min: number, max: number, errorText: string) {
    super(errorText);
    this.min = min;
    this.max = max;
  }

  public validate(value: string): boolean {
    const stripped = value.replace(/[^0-9.]/g, '');
    if (stripped === '' || Number.isNaN(Number(stripped))) return false;
    const numeric = Number(stripped);
    return numeric >= this.min && numeric <= this.max;
  }
}

/**
 * Validates a license plate: 1–10 alphanumeric characters, no punctuation or spaces.
 *
 * Ported from the platform's canonical rule in `@parkingbase/dev-input`
 * (`licensePlateValidatorRegex = /^[a-zA-Z0-9]{1,10}$/`), which the legacy reservation site
 * applies on the same field. Reimplemented here rather than imported — this app builds its own
 * UI layer and must not depend on the old stack.
 *
 * ## Invariants:
 * - Accepts lower case as well as upper. Why: `uppercaseFormatter` only normalises what the
 *   parker TYPES. A plate loaded from the API into an edit form bypasses the formatter, so a
 *   stored lower-case value must not be reported as invalid on a screen that merely displayed it.
 * - Rejects the hyphen and the space, despite both appearing on real plates. Why: the platform
 *   stores plates unpunctuated and gate/enforcement matching keys off that form, so accepting a
 *   separator here would produce a record no downstream system can match. Parity with the legacy
 *   validator is the point — a plate this rejects is one the old site rejects too.
 * - Blank is INVALID, so it must never be the sole gate on an optional field; `required` decides
 *   whether the field may be left empty, and `BaseInput` runs validators only against what is
 *   there. Mirrors every other validator in this file.
 * - Tests the RAW value — deliberately does NOT trim, matching the legacy validator. Why:
 *   the field also carries `pattern="[a-zA-Z0-9]{1,10}"`, which is anchored and does not
 *   trim, so a trimming validator would call ` ABC123 ` valid while native constraint
 *   validation refused it — no inline message, and a form that will not submit. Nothing
 *   trims the plate before save either, so a padded value that slipped through would be
 *   STORED padded, which is the corrupt plate data this validator exists to stop.
 */
export class LicensePlateValidator extends InputValidator {
  constructor(errorText = 'Enter a valid license plate (letters and numbers only)') {
    super(errorText);
  }

  public validate(value: string): boolean {
    return /^[a-zA-Z0-9]{1,10}$/.test(value);
  }
}
